/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import {Group, DataStorage, EEmbededElementTypes, RenderDocument} from "js_renderer";
import {vec2, vec3} from "gl-matrix";
import Factory from "../factory";
import PointEntity from "./point_entity";

export interface ITBox {
    x: number;
    y: number;
    w: number;
    h: number;
    corners: any;
}

export default
abstract class GeoEntity extends PointEntity {
    protected _ro: any = {};
    private _highlightColor: string;
    private _highlight: string | null;
    private _bbox: ITBox;

    constructor(aRenderDocument: RenderDocument) {
        super(aRenderDocument);

        this.extendProperties({
            position: vec3.create(),
            visibilityImportance: 2 // default is set to 2 in order to filter more items out
        });

        this._bbox = {x: 0, y: 0,  w: 0, h: 0, corners: null};

        this._highlightColor = "#75BAFD";
        this._highlight = null;
        // let img = new Image();
        // img.onload() {
        //     aContext.drawImage(img, 0, 0);
        // }
        // img.src = this._properties.textureUrl;
    }

    public extendProperties(aProperties: any) {
        // extend the properties
        super.extendProperties(aProperties);

        if (this._properties.position !== undefined && aProperties.position !== undefined) {
            this._properties.position = vec3.clone(aProperties.position);
        }
    }

    public abstract getTypeIcon(): CanvasImageSource;

    public getFont(): string {
        return 'Optima, Segoe, Candara, Calibri, Arial, sans-serif';
    }

    public getFontSize(): number {
        return 12;
    }

    public getIconSize(): number {
        return 20;
    }

    public getThickOffset(): number {
        return 0.5;
    }

    public getVerticalOffset(): number {
        return 20; // 50
    }

    public highlight(aStatus: boolean) {
        this._highlight = aStatus ? this._highlightColor : null;
    }

    public renderTest(aContext: CanvasRenderingContext2D, aVisibleArray: any[], aPosition: vec2, aDistanceFactor: number) {
        this._ro = {};
        this._ro.fontSize = this.getFontSize();
        this._ro.iconSize = this.getIconSize();
        this._ro.fillColor = this.getColor();
        this._ro.leg = true;
        this._ro.center = false;
        this._ro.border = 1;
        this._ro.x = aPosition[0];
        this._ro.y = aPosition[1];

        // distance LOD
        if (aDistanceFactor < 0.09) {
            this._ro.fontSize -= 4;
            this._ro.iconSize -= 9;
        } else if (aDistanceFactor < 0.5) {
            this._ro.fontSize -= 2;
            this._ro.iconSize -= 5;
        }

        // measure text
        aContext.font = this.getStyle() + " " + this._ro.fontSize + "pt " + this.getFont();
        this._ro.textWidth = aContext.measureText(this.getTitle()).width;
        this._ro.textHeight = this.getTextHeight(this.getFont(), this._ro.fontSize);

        this._ro.iconSizeHalf = this._ro.iconSize * 0.5;
        this._ro.iconSizeQuat = this._ro.iconSizeHalf * 0.5;

        this._ro.textX = this._ro.x + this._ro.iconSizeHalf + this._ro.iconSizeQuat;
        this._ro.textY = this._ro.y - this.getVerticalOffset();

        if (this.isTextBBoxVisible(aContext, aVisibleArray, this._ro) && this._renderDocumentProperties.renderElement.getVisibility()) {
            aVisibleArray.push(this);
        }
    }

    public abstract getStyle(): string;

    public render(aContext: CanvasRenderingContext2D) {
        aContext.font = this.getStyle() + " " + this._ro.fontSize + "pt " + this.getFont();

        // fill text - draw a black outline first
        aContext.fillStyle = 'rgb(0, 0, 0)';
        aContext.fillText(this.getTitle(), this._ro.textX - 0, this._ro.textY - this._ro.border);
        aContext.fillText(this.getTitle(), this._ro.textX - this._ro.border, this._ro.textY - 0);
        aContext.fillText(this.getTitle(), this._ro.textX + this._ro.border, this._ro.textY + 0);
        aContext.fillText(this.getTitle(), this._ro.textX + 0, this._ro.textY + this._ro.border);

        aContext.fillText(this.getTitle(), this._ro.textX - this._ro.border, this._ro.textY - this._ro.border);
        aContext.fillText(this.getTitle(), this._ro.textX - this._ro.border, this._ro.textY + this._ro.border);
        aContext.fillText(this.getTitle(), this._ro.textX + this._ro.border, this._ro.textY - this._ro.border);
        aContext.fillText(this.getTitle(), this._ro.textX + this._ro.border, this._ro.textY + this._ro.border);

        if (this._highlight === null) {
            aContext.fillStyle = 'rgb(' + Math.floor(this._ro.fillColor[0] * 255.0) + ',' + Math.floor(this._ro.fillColor[1] * 255.0) + ',' + Math.floor(this._ro.fillColor[2] * 255.0) + ')';
        } else {
            aContext.fillStyle = this._highlight;
        }
        // fill text
        aContext.fillText(this.getTitle(), this._ro.textX, this._ro.textY);

        if (this._ro.leg) {
            // draw a leg
            // aContext.save();
            // aContext.beginPath();
            // aContext.lineWidth = "1.0";
            // aContext.strokeStyle = 'rgb(255, 255, 255)';
            // aContext.moveTo(this._ro.x + this.getThickOffset(), this._ro.y + this.getThickOffset());
            // aContext.lineTo(this._ro.x + this.getThickOffset(), this._ro.y - this.getVerticalOffset() + this.getThickOffset() - (this._ro.textHeight / 2));
            // aContext.stroke();
            // aContext.restore();

            // draw an icon
            // TB note: it is better (nicer result) to draw 2 filled circles instead of one with a stroked border
            aContext.beginPath();
            aContext.save();
            aContext.fillStyle = '#000000';
            aContext.arc(this._ro.x, this._ro.textY - (this._ro.textHeight * 0.5), this._ro.iconSizeHalf + 1, 0, 2 * Math.PI, false);
            aContext.fill();
            aContext.restore();

            aContext.beginPath();
            aContext.arc(this._ro.x, this._ro.textY - (this._ro.textHeight * 0.5), this._ro.iconSizeHalf, 0, 2 * Math.PI, false);
            aContext.fill();
            let iconWidth = this._ro.iconSizeHalf + 3;
            aContext.drawImage(this.getTypeIcon(), this._ro.x - (iconWidth * 0.5), this._ro.y - this.getVerticalOffset() - (this._ro.textHeight * 0.5) - (iconWidth * 0.5), iconWidth, iconWidth);
        }
    }

    public getTextHeight(aFont: string, aFontSize: number): number {
        let text: any = document.createElement('span');
        text.innerHTML = 'Hg';
        text.style.fontFamily = aFont;
        text.style.fontSize = aFontSize + 'px';

        let block: any = document.createElement('div');
        block.style.display = 'inline-block';
        block.style.width = '1px';
        block.style.height = '1px';

        let div = document.createElement('div');
        div.appendChild(text);
        div.appendChild(block);
        document.body.appendChild(div);

        let result: any = {};
        try {
            block.offset = {
                top: block.getBoundingClientRect().top + document.body.scrollTop,
                left: block.getBoundingClientRect().left + document.body.scrollLeft
            };

            text.offset = {
                top: text.getBoundingClientRect().top + document.body.scrollTop,
                left: text.getBoundingClientRect().left + document.body.scrollLeft
            };

            block.style.verticalAlign = 'baseline';
            result.ascent = block.offset.top - text.offset.top;

            block.style.verticalAlign = 'bottom';
            result.height = block.offset.top - text.offset.top;

            result.descent = result.height - result.ascent;
        } finally {
            document.body.removeChild(div);
        }

        return result.height; // result;
    }

    public computeTextBBoxInternal(aX: number, aY: number, aW: number, aH: number): ITBox {
        this._bbox = {x: aX, y: aY, w: aW, h: aH, corners: null};

        this._bbox.corners = [{x: aX, y: aY},
            {x: aX + aW, y: aY},
            {x: aX + aW, y: aY + aH},
            {x: aX, y: aY + aH}];

        return this._bbox;
    }

    public computeTextBBox(aRO: any): ITBox {
        if (!aRO.center) {
            return this.computeTextBBoxInternal(aRO.textX - aRO.iconSize - aRO.iconSizeQuat,
                aRO.textY - (aRO.textHeight * 0.5) - aRO.iconSizeHalf,
                aRO.textWidth + aRO.iconSize + aRO.iconSizeQuat,
                aRO.iconSize);
        } else {
            return this.computeTextBBoxInternal(aRO.textX - aRO.iconSize - aRO.iconSizeQuat,
                aRO.textY - aRO.textHeight,
                aRO.textWidth + aRO.iconSize + aRO.iconSizeQuat,
                aRO.textHeight);
        }
    }

    public containsPoint(aX: number, aY: number) {
        return (aX > this._bbox.x && aX < this._bbox.x + this._bbox.w &&
            aY > this._bbox.y && aY < this._bbox.y + this._bbox.h);
    }

    public isTextBBoxVisible(aContext: CanvasRenderingContext2D, aVisibleArray: any, aRO: any) {
        let visible = true;

        this.computeTextBBox(aRO);

        for (let i = 0; i < aVisibleArray.length; i++) {
            let r = aVisibleArray[i];

            aContext.beginPath();
            aContext.rect(this._bbox.x, this._bbox.y, this._bbox.w, this._bbox.h);
            for (let v in r._bbox.corners) {
                let vert = r._bbox.corners[v];
                if (aContext.isPointInPath(vert.x, vert.y)) {
                    visible = false;
                    break;
                }
            }

            if (visible) {
                aContext.beginPath();
                aContext.rect(r._bbox.x, r._bbox.y, r._bbox.w, r._bbox.h);

                for (let v in this._bbox.corners) {
                    let vert = this._bbox.corners[v];
                    if (aContext.isPointInPath(vert.x, vert.y)) {
                        visible = false;
                        break;
                    }
                }
            }
        }
        // cadwork.webgl.Debug.logInfo(this.getTitle() + ': ' + visible);
        return visible;
    }
}
